/**
 * Custom hook for managing irrigation plan configuration form
 * Handles form state, validation, and CRUD operations for plan data
 */

import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import type { IrrigationPlanFormData } from "@/pages/main/IrrigationPlanWizardPage";
import type { PlanOverlapValidationResult, PlanScheduleLike } from "@/pages/main/utils/irrigationPlanOverlap";
import {
  ensureValidPlanSchedule,
  getResumptionHint,
  minutesToTimeString,
  validatePlanEndAgainstPlans,
  validatePlanStartAgainstPlans,
} from "@/pages/main/utils/irrigationPlanOverlap";
import { createIrrigationPlanAtom, deleteIrrigationPlanAtom, updateIrrigationPlanAtom } from "@/store/crud";
import { irrigationPlanByIdAtom, projectByIdAtom, refetchDataAtom } from "@/store/data";
import { showSuccessToastAtom } from "@/store/ui";
import { formatDays } from "@/utils/format";
import { IRRIGATION_DEFAULTS } from "../utils/constants";

// Use any to avoid type mismatches with AUT types
type IrrigationPlan = any;

import { getDisabledReason, validatePlanFormData } from "../utils/validation";

interface UseIrrigationPlanFormProps {
  /** ID of the plan being edited ("new" for create mode) */
  planId: string;
  /** Whether this is create mode */
  isCreateMode: boolean;
  /** ID of the project this plan belongs to */
  projectId: string | null;
  /** The current plan data (null in create mode) */
  plan: IrrigationPlan | null;
  /** Schedules of other plans for validation */
  otherPlanSchedules: PlanScheduleLike[];
  /** Minimum gap required between plans in minutes */
  minimumGapMinutes: number;
  /** Whether schedule resumption is enabled for the project's LIC */
  resumptionEnabled: boolean;
  /** Minimum gap (minutes) applied when resumption is disabled */
  withoutResumptionGap: number;
  /** Total duration for this plan in seconds */
  totalDurationSeconds: number;
  /** Whether any project is currently running */
  anyProjectRunning: boolean;
}

interface UseIrrigationPlanFormReturn {
  // State
  planData: IrrigationPlanFormData;
  hasChanges: boolean;
  canSavePlan: boolean;
  startValidationMessage: string | null;
  resumptionHint: string | null;
  overlapValidationValid: boolean;
  showDiscardModal: boolean;
  error: string | null;

  // Handlers
  handlePlanDataChange: (updates: Partial<IrrigationPlanFormData>) => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  handleDelete: () => Promise<void>;
  handleConfirmDiscard: () => void;
  handleCancelDiscard: () => void;
  setHasChanges: (value: boolean) => void;
}

/**
 * Hook for managing irrigation plan form state and operations
 */
export function useIrrigationPlanForm({
  planId,
  isCreateMode,
  projectId,
  plan,
  otherPlanSchedules,
  minimumGapMinutes,
  resumptionEnabled,
  withoutResumptionGap,
  totalDurationSeconds,
  anyProjectRunning,
}: UseIrrigationPlanFormProps): UseIrrigationPlanFormReturn {
  const [, setLocation] = useLocation();

  // CRUD atoms
  const createPlan = useSetAtom(createIrrigationPlanAtom);
  const updatePlan = useSetAtom(updateIrrigationPlanAtom);
  const deletePlan = useSetAtom(deleteIrrigationPlanAtom);
  const refetchData = useSetAtom(refetchDataAtom);
  const showSuccessToast = useSetAtom(showSuccessToastAtom);

  // Form state
  const [planData, setPlanData] = useState<IrrigationPlanFormData>({
    name: "",
    description: "",
    startTime: IRRIGATION_DEFAULTS.DEFAULT_START_TIME,
    daysOfWeek: [],
    isEnabled: true,
    fertigationEnabled: false,
    backwashEnabled: false,
    startDate: "",
    endDate: "",
  });
  const [hasChanges, setHasChanges] = useState(isCreateMode);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current plan ID for validation (null in create mode)
  const currentPlanId = !isCreateMode && planId !== "new" ? planId : null;

  // Validation: Check start time against other plans
  const startValidationResult: PlanOverlapValidationResult = useMemo(() => {
    if (!planData.isEnabled) {
      return { isValid: true };
    }

    if (!planData.startTime || !planData.daysOfWeek || planData.daysOfWeek.length === 0) {
      return { isValid: true };
    }

    return validatePlanStartAgainstPlans(
      {
        id: currentPlanId ?? undefined,
        name: planData.name,
        daysOfWeek: planData.daysOfWeek,
        startTime: planData.startTime,
      },
      otherPlanSchedules,
      minimumGapMinutes
    );
  }, [
    currentPlanId,
    otherPlanSchedules,
    planData.daysOfWeek,
    planData.name,
    planData.startTime,
    planData.isEnabled,
    minimumGapMinutes,
  ]);

  const totalDurationMinutes = useMemo(() => {
    return Math.max(0, Math.ceil(totalDurationSeconds / 60));
  }, [totalDurationSeconds]);

  const endValidationResult: PlanOverlapValidationResult = useMemo(() => {
    if (!planData.isEnabled) {
      return { isValid: true };
    }

    if (!planData.startTime || !planData.daysOfWeek || planData.daysOfWeek.length === 0) {
      return { isValid: true };
    }

    // Even with zero duration (no steps yet), we must validate that the plan’s
    // start/end point does not sit too close to another plan’s start.
    // For zero-duration plans, start == end, so this still works.
    return validatePlanEndAgainstPlans(
      {
        id: currentPlanId ?? undefined,
        name: planData.name,
        daysOfWeek: planData.daysOfWeek,
        startTime: planData.startTime,
        totalDurationMinutes,
      },
      otherPlanSchedules,
      minimumGapMinutes
    );
  }, [
    currentPlanId,
    otherPlanSchedules,
    planData.daysOfWeek,
    planData.isEnabled,
    planData.name,
    planData.startTime,
    minimumGapMinutes,
    totalDurationMinutes,
  ]);

  const overlapValidationValid = startValidationResult.isValid && endValidationResult.isValid;

  // Validation: User-friendly message for start time conflicts
  const startValidationMessage = useMemo(() => {
    if (!startValidationResult.isValid && startValidationResult.conflict) {
      const { conflict } = startValidationResult;
      const dayLabel = formatDays([conflict.day]);
      const requiredStart = minutesToTimeString(conflict.other.endMinutes + conflict.minimumGapMinutes);

      return `Conflito com o plano "${conflict.other.name}" em ${dayLabel}. Inicie após ${requiredStart} para manter ${conflict.minimumGapMinutes} minutos de intervalo.`;
    }

    if (!endValidationResult.isValid && endValidationResult.conflict) {
      const { conflict } = endValidationResult;
      const dayLabel = formatDays([conflict.day]);
      const latestAllowedEnd = minutesToTimeString(conflict.other.startMinutes - conflict.minimumGapMinutes);
      const nextPlanStart = minutesToTimeString(conflict.other.startMinutes);

      return `Conflito com o plano "${conflict.other.name}" em ${dayLabel}. Finalize até ${latestAllowedEnd} para manter ${conflict.minimumGapMinutes} minutos antes do início às ${nextPlanStart}.`;
    }

    return null;
  }, [endValidationResult, startValidationResult]);

  // Optional hint: disabling resumption would resolve the conflict (only when it actually would).
  const resumptionHint = useMemo(() => {
    const activeConflict = startValidationResult.conflict ?? endValidationResult.conflict;
    if (!activeConflict) return null;
    return getResumptionHint(activeConflict, { resumptionEnabled, withoutResumptionGap });
  }, [startValidationResult.conflict, endValidationResult.conflict, resumptionEnabled, withoutResumptionGap]);

  // Validation: Check if form data is valid
  const formValidationResult = useMemo(() => validatePlanFormData(planData), [planData]);
  const planDataIsValid = formValidationResult.isValid;

  // Validation: Can save if all validations pass and no project is running
  const canSavePlan = planDataIsValid && overlapValidationValid && !anyProjectRunning;

  // Load plan data when plan changes (skip in create mode)
  useEffect(() => {
    if (!isCreateMode && plan) {
      // Convert API model to form data
      setPlanData({
        name: plan.name,
        description: plan.description || "",
        startTime: plan.start_time,
        daysOfWeek: plan.days_of_week,
        isEnabled: plan.is_enabled,
        fertigationEnabled: plan.fertigation_enabled,
        backwashEnabled: plan.backwash_enabled,
        startDate: plan.start_date || "",
        endDate: plan.end_date || "",
      });

      setHasChanges(false);
      setError(null);
    }
  }, [plan, isCreateMode]);

  /**
   * Handle plan data changes
   */
  const handlePlanDataChange = useCallback((updates: Partial<IrrigationPlanFormData>) => {
    setPlanData((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
    setError(null);
  }, []);

  /**
   * Handle save operation (create or update)
   */
  const handleSave = async () => {
    if (!projectId) return;
    if (!canSavePlan) return;

    setError(null);

    try {
      // Convert form data to API model format
      const planDataToSave = {
        name: planData.name,
        description: planData.description || null,
        start_time: planData.startTime,
        days_of_week: planData.daysOfWeek,
        is_enabled: planData.isEnabled,
        fertigation_enabled: planData.fertigationEnabled,
        backwash_enabled: planData.backwashEnabled,
        start_date: planData.startDate || null,
        end_date: planData.endDate || null,
      };

      if (isCreateMode) {
        // CREATE: Create new plan with empty steps
        const result = await createPlan({
          ...planDataToSave,
          project: projectId,
          steps: [], // Create with empty steps initially
        });

        const newPlanId = result.data?.id;
        if (!newPlanId) {
          throw new Error("Failed to create irrigation plan");
        }

        // Navigate to edit mode with sectors tab visible
        setLocation(`/app/irrigation-plans/${newPlanId}?activeTab=sectors`);
      } else {
        // UPDATE: Update existing plan
        await updatePlan({ id: planId, data: planDataToSave });
        // Local-first approach: Don't refetch, trust the operation succeeded
        setHasChanges(false);
        showSuccessToast({ message: "Plano de irrigação salvo com sucesso!" });
        setLocation(`/app/irrigation-plans`);
      }
    } catch (err) {
      const message = "Erro ao salvar plano. Tente novamente.";
      setError(message);
      // Toast is already shown by withOperationHandlingAtom
    }
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = () => {
    if (hasChanges) {
      setShowDiscardModal(true);
      return;
    }

    // Navigate back to appropriate page
    if (isCreateMode) {
      setLocation("/app/irrigation-plans");
    } else {
      setLocation(`/app/projects/${projectId}`);
    }
  };

  /**
   * Handle delete operation
   */
  const handleDelete = async () => {
    // Prevent deletion if in create mode or missing ID
    if (isCreateMode || !planId || planId === "new") return;

    setError(null);

    try {
      await deletePlan(planId);

      showSuccessToast({ message: "Plano de irrigação apagado com sucesso!" });

      // Redirect back to the project page (or irrigation plans list)
      if (projectId) {
        setLocation(`/app/projects/${projectId}`);
      } else {
        setLocation(`/app/irrigation-plans`);
      }
    } catch (err) {
      setError("Erro ao apagar o plano. Tente novamente.");
      // Toast is already shown by withOperationHandlingAtom for the error
    }
  };

  /**
   * Confirm discard changes
   */
  const handleConfirmDiscard = () => {
    setShowDiscardModal(false);

    // Navigate back to appropriate page
    if (isCreateMode) {
      setLocation("/app/irrigation-plans");
    } else {
      setLocation(`/app/projects/${projectId}`);
    }
  };

  /**
   * Cancel discard changes
   */
  const handleCancelDiscard = () => {
    setShowDiscardModal(false);
  };

  return {
    // State
    planData,
    hasChanges,
    canSavePlan,
    startValidationMessage,
    resumptionHint,
    overlapValidationValid,
    showDiscardModal,
    error,

    // Handlers
    handlePlanDataChange,
    handleSave,
    handleCancel,
    handleDelete,
    handleConfirmDiscard,
    handleCancelDiscard,
    setHasChanges,
  };
}
